import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, AlertIcon, Badge, Box, Button, Center, Checkbox, Flex, FormControl, FormHelperText, FormLabel,
  HStack, IconButton, Input, InputGroup, InputLeftAddon, InputRightAddon, Modal, ModalBody, ModalCloseButton,
  ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, SimpleGrid, Spinner, Text, Tooltip, Wrap,
  WrapItem, useToast,
} from "@chakra-ui/react";
import { Pause, Pencil, Play, Plus, RefreshCw } from "lucide-react";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip as ChartTooltip, Legend, Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import ConfirmDialog from "../ConfirmDialog";
import { criarMlAdsCampanha, editarMlAdsCampanha, getMlAdsPainel } from "../../services/api";
import {
  ACCENT, ESTRATEGIAS, Kpi, ROAS_EQUILIBRIO, ROAS_OBJETIVO_PADRAO, StatusCampanha, VERDE, VERMELHO, Vazio,
  brl, corRoas, dataBR, erroMsg, num, pct, roasTxt,
} from "./comum";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend, Filler);

const COR_STATUS = { active: "green.400", paused: "yellow.400", deleted: "gray.300" };

function Metrica({ label, valor, cor }) {
  return (
    <Box minW={0}>
      <Text fontSize="2xs" color="gray.500" textTransform="uppercase" noOfLines={1}>{label}</Text>
      <Text fontSize="sm" fontWeight="bold" color={cor || "gray.700"} noOfLines={1}>{valor}</Text>
    </Box>
  );
}

function GraficoDiario({ diario }) {
  const dados = useMemo(() => ({
    labels: diario.map((d) => dataBR(d.data).slice(0, 5)),
    datasets: [
      {
        label: "Investimento",
        data: diario.map((d) => d.cost || 0),
        borderColor: VERMELHO,
        backgroundColor: "rgba(197, 48, 48, 0.08)",
        fill: true,
        tension: 0.3,
        pointRadius: 2,
      },
      {
        label: "Receita (vendas por anúncio)",
        data: diario.map((d) => d.total_amount || 0),
        borderColor: VERDE,
        backgroundColor: "rgba(1, 105, 94, 0.08)",
        fill: true,
        tension: 0.3,
        pointRadius: 2,
      },
    ],
  }), [diario]);

  const opcoes = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${brl(ctx.parsed.y)}` } },
    },
    scales: {
      x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 10, font: { size: 10 } }, grid: { display: false } },
      y: { beginAtZero: true, ticks: { font: { size: 10 }, callback: (v) => `R$ ${num(v)}` } },
    },
  }), []);

  return (
    <Box h={{ base: "220px", md: "280px" }}>
      <Line data={dados} options={opcoes} />
    </Box>
  );
}

function CartaoCampanha({ c, conta, onEditar, onAlternar }) {
  const m = c.metricas || {};
  const excluida = c.status === "deleted";
  return (
    <Box bg="white" borderRadius="lg" boxShadow="sm" p={4} borderLeft="4px solid" borderLeftColor={COR_STATUS[c.status] || "gray.300"}
      opacity={excluida ? 0.7 : 1}>
      <Flex justify="space-between" align="flex-start" gap={2}>
        <Box minW={0}>
          <HStack spacing={2} flexWrap="wrap">
            <Text fontWeight="bold" noOfLines={1}>{c.nome}</Text>
            <StatusCampanha status={c.status} />
          </HStack>
          <Text fontSize="xs" color="gray.500" noOfLines={1}>{conta.nome} · #{c.id}</Text>
        </Box>
        {!excluida && (
          <HStack spacing={1} flexShrink={0}>
            <Tooltip label={c.status === "active" ? "Pausar campanha" : "Ativar campanha"} hasArrow>
              <IconButton size="sm" variant="outline" aria-label={c.status === "active" ? "Pausar" : "Ativar"}
                icon={c.status === "active" ? <Pause size={14} /> : <Play size={14} />}
                colorScheme={c.status === "active" ? "yellow" : "green"} onClick={() => onAlternar(c, conta)} />
            </Tooltip>
            <Tooltip label="Editar orçamento, ROAS e estratégia" hasArrow>
              <IconButton size="sm" variant="outline" aria-label="Editar" icon={<Pencil size={14} />} onClick={() => onEditar(c, conta)} />
            </Tooltip>
          </HStack>
        )}
      </Flex>

      <Wrap spacing={2} mt={2}>
        {c.orcamento !== null && <WrapItem><Badge variant="subtle">Orçamento {brl(c.orcamento)}/dia</Badge></WrapItem>}
        {c.roas_objetivo !== null && <WrapItem><Badge variant="subtle">ROAS objetivo {roasTxt(c.roas_objetivo)}</Badge></WrapItem>}
        {c.estrategia && <WrapItem><Badge variant="subtle">{ESTRATEGIAS[c.estrategia]?.label || c.estrategia}</Badge></WrapItem>}
        {c.regra && (
          <WrapItem>
            <Badge colorScheme="purple" variant="subtle">
              Regra: {c.regra.nome}{c.regra.simulacao ? " (simulação)" : ""}{!c.regra.ativa ? " (desligada)" : ""}
            </Badge>
          </WrapItem>
        )}
        {c.anuncios_regra !== null && c.anuncios_regra !== undefined && (
          <WrapItem><Badge colorScheme="blue" variant="subtle">{num(c.anuncios_regra)} anúncio(s) acompanhados</Badge></WrapItem>
        )}
      </Wrap>

      <SimpleGrid columns={{ base: 3, sm: 6 }} spacing={2} mt={3} pt={3} borderTop="1px solid" borderColor="gray.100">
        <Metrica label="Investimento" valor={brl(m.cost)} cor={VERMELHO} />
        <Metrica label="Receita" valor={brl(m.total_amount)} cor={VERDE} />
        <Metrica label="ROAS" valor={roasTxt(m.roas)} cor={corRoas(m.roas)} />
        <Metrica label="CTR" valor={pct(m.ctr)} />
        <Metrica label="Cliques" valor={num(m.clicks)} />
        <Metrica label="Vendas" valor={num(m.advertising_items_quantity)} />
      </SimpleGrid>
    </Box>
  );
}

function CampanhaModal({ aberto, onFechar, edicao, contas, contaPadrao, onSalvo }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    if (edicao) {
      setForm({
        conta_id: edicao.conta.id,
        nome: edicao.c.nome,
        orcamento: edicao.c.orcamento ?? "",
        estrategia: edicao.c.estrategia || "increase",
        roas_objetivo: edicao.c.roas_objetivo ?? ROAS_OBJETIVO_PADRAO,
      });
    } else {
      setForm({
        conta_id: contaPadrao || contas[0]?.id || 1,
        nome: "",
        orcamento: 30,
        estrategia: "increase",
        roas_objetivo: ROAS_OBJETIVO_PADRAO,
        status: "paused",
      });
    }
  }, [aberto, edicao, contas, contaPadrao]);

  const roas = Number(String(form.roas_objetivo ?? "").replace(",", "."));
  const acosEquivalente = roas > 0 ? 100 / roas : null;

  const salvar = async () => {
    setSalvando(true);
    try {
      const dados = {
        nome: String(form.nome || "").trim(),
        orcamento: Number(String(form.orcamento).replace(",", ".")),
        estrategia: form.estrategia,
        roas_objetivo: roas,
      };
      if (edicao) {
        const mudou = {};
        if (dados.nome !== edicao.c.nome) mudou.nome = dados.nome;
        if (dados.orcamento !== edicao.c.orcamento) mudou.orcamento = dados.orcamento;
        if (dados.estrategia !== edicao.c.estrategia) mudou.estrategia = dados.estrategia;
        if (dados.roas_objetivo !== edicao.c.roas_objetivo) mudou.roas_objetivo = dados.roas_objetivo;
        if (Object.keys(mudou).length === 0) {
          onFechar();
          return;
        }
        await editarMlAdsCampanha(edicao.conta.id, edicao.c.id, mudou);
        toast({ title: "Campanha atualizada no Mercado Livre.", status: "success", duration: 3000 });
      } else {
        await criarMlAdsCampanha({ ...dados, conta_id: Number(form.conta_id), status: form.status });
        toast({ title: "Campanha criada no Mercado Livre.", status: "success", duration: 3000 });
      }
      onSalvo();
      onFechar();
    } catch (e) {
      toast({ title: "Não foi possível salvar", description: erroMsg(e), status: "error", duration: 8000, isClosable: true });
    } finally {
      setSalvando(false);
    }
  };

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  return (
    <Modal isOpen={aberto} onClose={onFechar} size={{ base: "full", md: "lg" }} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{edicao ? "Editar campanha" : "Nova campanha"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!edicao && (
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Conta</FormLabel>
              <Select size="sm" value={form.conta_id || ""} onChange={set("conta_id")}>
                {contas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </Select>
            </FormControl>
          )}
          <FormControl mb={3} isRequired>
            <FormLabel fontSize="sm">Nome</FormLabel>
            <Input size="sm" value={form.nome || ""} onChange={set("nome")} placeholder="Ex.: BL PARTS - Automática" maxLength={100} />
          </FormControl>
          <FormControl mb={3} isRequired>
            <FormLabel fontSize="sm">Orçamento diário</FormLabel>
            <InputGroup size="sm">
              <InputLeftAddon>R$</InputLeftAddon>
              <Input type="number" min={1} step="1" value={form.orcamento ?? ""} onChange={set("orcamento")} inputMode="decimal" />
              <InputRightAddon>/dia</InputRightAddon>
            </InputGroup>
            <FormHelperText fontSize="xs">
              Média do mês: num dia o ML pode gastar até o dobro, usando a sobra dos dias anteriores.
            </FormHelperText>
          </FormControl>
          <FormControl mb={3} isRequired>
            <FormLabel fontSize="sm">Estratégia</FormLabel>
            <Select size="sm" value={form.estrategia || "increase"} onChange={set("estrategia")}>
              {Object.entries(ESTRATEGIAS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </Select>
            <FormHelperText fontSize="xs">{ESTRATEGIAS[form.estrategia]?.ajuda}</FormHelperText>
          </FormControl>
          <FormControl mb={3} isRequired>
            <FormLabel fontSize="sm">ROAS objetivo</FormLabel>
            <InputGroup size="sm">
              <Input type="number" min={1} max={35} step="0.1" value={form.roas_objetivo ?? ""} onChange={set("roas_objetivo")} inputMode="decimal" />
              <InputRightAddon>x</InputRightAddon>
            </InputGroup>
            <FormHelperText fontSize="xs">
              Receita por real investido (1x a 35x).
              {acosEquivalente !== null && ` Equivale a gastar ${num(acosEquivalente, 1)}% da receita em anúncio.`}
            </FormHelperText>
            {roas > 0 && roas < ROAS_EQUILIBRIO && (
              <Alert status="warning" mt={2} fontSize="xs" borderRadius="md" py={2}>
                <AlertIcon />
                Abaixo de {num(ROAS_EQUILIBRIO, 1)}x a venda por anúncio não cobre a margem de 30% do preço.
              </Alert>
            )}
          </FormControl>
          {!edicao && (
            <FormControl mb={1}>
              <FormLabel fontSize="sm">Começar</FormLabel>
              <Select size="sm" value={form.status || "paused"} onChange={set("status")}>
                <option value="paused">Pausada (sem gasto)</option>
                <option value="active">Ativa (começa a gastar quando tiver anúncio)</option>
              </Select>
            </FormControl>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onFechar} isDisabled={salvando}>Cancelar</Button>
          <Button size="sm" bg={ACCENT} color="white" _hover={{ bg: "#e07728" }} onClick={salvar} isLoading={salvando}
            isDisabled={!String(form.nome || "").trim() || !(Number(form.orcamento) > 0) || !(roas >= 1 && roas <= 35)}>
            {edicao ? "Salvar no ML" : "Criar no ML"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function CampanhasTab({ conta, periodo, opcoes, onMudou }) {
  const toast = useToast();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [excluidas, setExcluidas] = useState(false);
  const [modal, setModal] = useState({ aberto: false, edicao: null });
  const [alternar, setAlternar] = useState(null);
  const [alternando, setAlternando] = useState(false);

  const carregar = useCallback(async (atualizar = false) => {
    setCarregando(true);
    const r = await getMlAdsPainel({
      conta: conta || undefined, de: periodo.de, ate: periodo.ate, excluidas: excluidas ? 1 : 0, atualizar: atualizar ? 1 : 0,
    });
    setDados(r);
    setCarregando(false);
    if (!r) toast({ title: "Não foi possível carregar as campanhas.", status: "error", duration: 5000 });
  }, [conta, periodo.de, periodo.ate, excluidas, toast]);

  useEffect(() => { carregar(); }, [carregar]);

  const contas = useMemo(() => (opcoes?.contas || []).filter((c) => c.anunciante), [opcoes]);
  const campanhas = useMemo(
    () => (dados?.contas || []).flatMap((ct) => (ct.campanhas || []).map((c) => ({ c, conta: ct }))),
    [dados]
  );
  const vivas = campanhas.filter(({ c }) => c.status !== "deleted");
  const t = dados?.total || {};

  const confirmarAlternar = async () => {
    if (!alternar) return;
    setAlternando(true);
    const novo = alternar.c.status === "active" ? "paused" : "active";
    try {
      await editarMlAdsCampanha(alternar.conta.id, alternar.c.id, { status: novo });
      toast({ title: novo === "active" ? "Campanha ativada." : "Campanha pausada.", status: "success", duration: 3000 });
      setAlternar(null);
      carregar(true);
      onMudou?.();
    } catch (e) {
      toast({ title: "O ML recusou", description: erroMsg(e), status: "error", duration: 8000, isClosable: true });
    } finally {
      setAlternando(false);
    }
  };

  return (
    <Box p={{ base: 3, md: 4 }}>
      <Flex justify="space-between" align="center" gap={2} mb={3} flexWrap="wrap">
        <Checkbox size="sm" isChecked={excluidas} onChange={(e) => setExcluidas(e.target.checked)}>
          <Text fontSize="sm">Mostrar excluídas</Text>
        </Checkbox>
        <HStack spacing={2}>
          <Button size="sm" variant="outline" leftIcon={<RefreshCw size={14} />} onClick={() => carregar(true)} isLoading={carregando}>
            Atualizar
          </Button>
          <Button size="sm" leftIcon={<Plus size={14} />} bg={ACCENT} color="white" _hover={{ bg: "#e07728" }}
            onClick={() => setModal({ aberto: true, edicao: null })} isDisabled={contas.length === 0}>
            Nova campanha
          </Button>
        </HStack>
      </Flex>

      {carregando && !dados && <Center py={12}><Spinner color={ACCENT} /></Center>}

      {dados && (
        <>
          {(dados.contas || []).filter((c) => c.erro).map((c) => (
            <Alert key={c.id} status="warning" mb={3} borderRadius="md" fontSize="sm">
              <AlertIcon />
              {c.nome}: {c.erro}
            </Alert>
          ))}

          <SimpleGrid columns={{ base: 2, md: 4, xl: 8 }} spacing={3} mb={4}>
            <Kpi label="Investimento" valor={brl(t.cost)} cor={VERMELHO} />
            <Kpi label="Receita" valor={brl(t.total_amount)} ajuda="vendas atribuídas aos anúncios" />
            <Kpi label="ROAS" valor={roasTxt(t.roas)} ajuda={`objetivo ${num(ROAS_OBJETIVO_PADRAO)}x · equilíbrio ${num(ROAS_EQUILIBRIO, 1)}x`}
              cor={t.roas === null || t.roas === undefined ? "gray" : t.roas >= ROAS_EQUILIBRIO ? VERDE : VERMELHO} />
            <Kpi label="ACOS" valor={pct(t.acos, 1)} ajuda="% da receita gasto em anúncio" cor={ACCENT} />
            <Kpi label="Vendas" valor={num(t.advertising_items_quantity)} ajuda="por anúncio (diretas + indiretas)" />
            <Kpi label="Cliques" valor={num(t.clicks)} ajuda={`CPC ${t.cpc !== null && t.cpc !== undefined ? brl(t.cpc) : "—"}`} cor="#2B6CB0" />
            <Kpi label="Impressões" valor={num(t.prints)} cor="#2B6CB0" />
            <Kpi label="CTR" valor={pct(t.ctr)} ajuda="cliques ÷ impressões" cor="#2B6CB0" />
          </SimpleGrid>

          {(dados.diario || []).length > 0 && (
            <Box bg="white" borderRadius="lg" boxShadow="sm" p={4} mb={4}>
              <Text fontWeight="bold" fontSize="sm" mb={2}>Investimento × receita por dia</Text>
              <GraficoDiario diario={dados.diario} />
            </Box>
          )}

          {vivas.length === 0 && (
            <Box bg="white" borderRadius="lg" boxShadow="sm" mb={4}>
              <Vazio titulo="Nenhuma campanha ativa ou pausada">
                {campanhas.length > 0
                  ? "As campanhas listadas abaixo estão excluídas no Mercado Livre e não rodam mais."
                  : "Crie uma campanha e uma regra para os anúncios entrarem nela sozinhos."}
              </Vazio>
            </Box>
          )}

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
            {campanhas.map(({ c, conta: ct }) => (
              <CartaoCampanha key={`${ct.id}-${c.id}`} c={c} conta={ct}
                onEditar={(cc, cta) => setModal({ aberto: true, edicao: { c: cc, conta: cta } })}
                onAlternar={(cc, cta) => setAlternar({ c: cc, conta: cta })} />
            ))}
          </SimpleGrid>

          <Text fontSize="xs" color="gray.400" mt={3}>
            Período {dataBR(dados.de)} a {dataBR(dados.ate)}. O ML fecha as métricas do dia anterior às 10h e atribui vendas até 14 dias depois do clique.
          </Text>
        </>
      )}

      <CampanhaModal aberto={modal.aberto} edicao={modal.edicao} contas={contas} contaPadrao={conta ? Number(conta) : null}
        onFechar={() => setModal({ aberto: false, edicao: null })} onSalvo={() => { carregar(true); onMudou?.(); }} />

      <ConfirmDialog
        isOpen={!!alternar}
        onClose={() => setAlternar(null)}
        onConfirm={confirmarAlternar}
        isLoading={alternando}
        title={alternar?.c.status === "active" ? "Pausar campanha" : "Ativar campanha"}
        confirmText={alternar?.c.status === "active" ? "Pausar" : "Ativar"}
        confirmColor={alternar?.c.status === "active" ? "yellow" : "green"}
        body={alternar?.c.status === "active"
          ? `"${alternar?.c.nome}" para de aparecer e de gastar agora.`
          : `"${alternar?.c.nome}" volta a gastar até ${brl(alternar?.c.orcamento)}/dia em média (num dia pode chegar ao dobro).`}
      />
    </Box>
  );
}
